import * as Immutable from 'immutable';

import type {
    ImMNElement,
    MNDrawingContent,
    MNImageBackgroundRemovalContent,
    MNImageContent,
    MNImageEditorDataContent,
} from '../../../../../common/elements/elementModelTypes';
import { MNImageBackgroundRemovalVariantOptions } from '../../../../../common/elements/elementModelTypes';
import { asObject, type ImmutableMap } from '../../../../../common/utils/immutableHelper';
import {
    getDrawingData,
    getImageBackgroundRemovalProp,
    getImageOriginalProp,
    getImageProp,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import {
    getImageEditorScale,
    getImageEditorDataScaleXOrDefault,
    getImageEditorDataScaleYOrDefault,
} from '../imageModalUtils';
import { getDrawingTransformResult, type DrawingTransform } from '../utils/imageDrawingTransformUtils';

export const BackgroundRemovalVariantOptions = MNImageBackgroundRemovalVariantOptions;
export type BackgroundRemovalVariantOptions = MNImageBackgroundRemovalVariantOptions;

/**
 * Read background-removal metadata as a plain object.
 */
const getBackgroundRemoval = (element: ImMNElement): MNImageBackgroundRemovalContent =>
    asObject<MNImageBackgroundRemovalContent>(getImageBackgroundRemovalProp(element)) || {};

/**
 * Read the current image metadata as a plain object. Elements mid-upload have no image yet.
 */
const getCurrentImage = (element: ImMNElement): MNImageContent | undefined =>
    asObject<MNImageContent>(getImageProp(element));

const getBackgroundRemovalSourceFromContent = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => backgroundRemoval.sources?.[variant];

const getRenderedBackgroundRemovalImage = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined =>
    backgroundRemoval.rendered?.[variant] ?? getBackgroundRemovalSourceFromContent(backgroundRemoval, variant);

/**
 * Check if both full-source variants are available.
 */
export const hasBackgroundRemovalCache = (element: ImMNElement): boolean => {
    const backgroundRemoval = getBackgroundRemoval(element);
    return (
        !!getBackgroundRemovalSourceFromContent(backgroundRemoval, BackgroundRemovalVariantOptions.withBackground) &&
        !!getBackgroundRemovalSourceFromContent(backgroundRemoval, BackgroundRemovalVariantOptions.withoutBackground)
    );
};

const hasTransparentMetadata = (image: MNImageContent | undefined): boolean =>
    Object.prototype.hasOwnProperty.call(image || {}, 'transparent');

/**
 * Check whether the source image originally uploaded to Milanote was transparent.
 *
 * Used to hide the background-removal controls for images that were already transparent when uploaded,
 * so it must never report `true` for an image that is only transparent *because* we removed its
 * background — that would hide the control the user needs to undo the removal.
 *
 * The cached background-shown source is the most trustworthy signal, so it is checked first: it is the
 * image as it was before any removal ran. `content.imageOriginal` cannot be used for this, despite the
 * name — it tracks the uncropped source of the *current* image, so cropping a background-removed image
 * replaces it with the transparent output.
 */
export const isOriginalUploadTransparent = (element: ImMNElement): boolean => {
    const withBackgroundSource = getBackgroundRemovalSourceFromContent(
        getBackgroundRemoval(element),
        BackgroundRemovalVariantOptions.withBackground,
    );
    if (hasTransparentMetadata(withBackgroundSource)) return !!withBackgroundSource?.transparent;

    // A removal has run but predates transparency metadata: treat the upload as opaque, so the controls
    // stay available.
    if (hasBackgroundRemovalCache(element)) return false;

    // No removal has ever run, so `imageOriginal` really is the uncropped upload.
    const originalImage = asObject<MNImageContent>(getImageOriginalProp(element));
    if (hasTransparentMetadata(originalImage)) return !!originalImage.transparent;

    return !!getCurrentImage(element)?.transparent;
};

const getInactiveVariant = (variant: BackgroundRemovalVariantOptions): BackgroundRemovalVariantOptions =>
    variant === BackgroundRemovalVariantOptions.withoutBackground
        ? BackgroundRemovalVariantOptions.withBackground
        : BackgroundRemovalVariantOptions.withoutBackground;

const getOriginalImageOrFallback = (image: MNImageContent | undefined): string | undefined =>
    image?.original || image?.large || image?.regular || image?.thumb;

const getActiveVariantFromBackgroundRemoval = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    currentImage: MNImageContent | undefined,
): BackgroundRemovalVariantOptions => {
    if (
        backgroundRemoval.active === BackgroundRemovalVariantOptions.withBackground ||
        backgroundRemoval.active === BackgroundRemovalVariantOptions.withoutBackground
    ) {
        return backgroundRemoval.active;
    }

    return getOriginalImageOrFallback(currentImage) ===
        getOriginalImageOrFallback(
            getRenderedBackgroundRemovalImage(backgroundRemoval, BackgroundRemovalVariantOptions.withoutBackground),
        )
        ? BackgroundRemovalVariantOptions.withoutBackground
        : BackgroundRemovalVariantOptions.withBackground;
};

const getBackgroundRemovalSources = (backgroundRemoval: MNImageBackgroundRemovalContent) => ({
    withBackground: getBackgroundRemovalSourceFromContent(
        backgroundRemoval,
        BackgroundRemovalVariantOptions.withBackground,
    ),
    withoutBackground: getBackgroundRemovalSourceFromContent(
        backgroundRemoval,
        BackgroundRemovalVariantOptions.withoutBackground,
    ),
});

/**
 * Get the background-removal variant that is currently active on the element.
 */
export const getActiveBackgroundRemovalVariant = (element: ImMNElement): BackgroundRemovalVariantOptions =>
    getActiveVariantFromBackgroundRemoval(getBackgroundRemoval(element), getCurrentImage(element));

/**
 * Check if the removed-background variant is currently active.
 */
export const isBackgroundRemovalActive = (element: ImMNElement): boolean =>
    getActiveBackgroundRemovalVariant(element) === BackgroundRemovalVariantOptions.withoutBackground;

/**
 * Get the full-size source for a background-removal variant.
 */
export const getBackgroundRemovalSource = (
    element: ImMNElement,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => getBackgroundRemovalSourceFromContent(getBackgroundRemoval(element), variant);

export const getBackgroundRemovalDisplayImage = (
    element: ImMNElement,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => getRenderedBackgroundRemovalImage(getBackgroundRemoval(element), variant);

/**
 * Check whether a variant has a render in the current crop coordinate space.
 */
export const hasBackgroundRemovalRenderedVariant = (
    element: ImMNElement,
    variant: BackgroundRemovalVariantOptions,
): boolean => !!getBackgroundRemoval(element).rendered?.[variant];

/**
 * Get the full-source image the editor should load for the active variant.
 */
export const getBackgroundRemovalEditSource = (element: ImMNElement): MNImageContent | undefined =>
    getBackgroundRemovalSource(element, getActiveBackgroundRemovalVariant(element));

/**
 * Get the full-source image for the inactive variant so crop/rotate can be mirrored.
 */
export const getBackgroundRemovalInactiveSource = (element: ImMNElement): MNImageContent | undefined =>
    getBackgroundRemovalSource(element, getInactiveVariant(getActiveBackgroundRemovalVariant(element)));

type BuildBackgroundRemovalCropChangesOptions = {
    element: ImMNElement;
    imageOriginal: MNImageContent;
    imageEditorData: MNImageEditorDataContent;
    clearDrawing: boolean;
    preserveBackgroundRemoval: boolean;
    drawingSessionTransform?: DrawingTransform;
};

type DrawingSaveChanges = {
    drawing?: MNDrawingContent | null;
    hasDrawing?: boolean;
};

/**
 * Resolve the element changes a save should apply to an existing drawing.
 *
 * - Rotation (`clearDrawing`) removes the drawing, since the overlay can't follow a quarter-turn.
 * - A horizontal/vertical flip transforms the stored drawing so it stays aligned with the flipped image.
 * - Otherwise the drawing is left untouched (no keys = no change).
 *
 * `flipScaleX`/`flipScaleY` are the net flip applied this session (±1): the new scale relative to the scale
 * already baked into the stored image.
 */
const getDrawingSaveChanges = (
    element: ImMNElement,
    clearDrawing: boolean,
    transform: DrawingTransform,
): DrawingSaveChanges => {
    if (clearDrawing) return { drawing: null, hasDrawing: false };

    const hasTransform = transform.rotation !== 0 || transform.scaleX === -1 || transform.scaleY === -1;
    const drawingData = getDrawingData(element);

    if (!hasTransform || !drawingData) return {};

    const currentDrawing = asObject<MNDrawingContent>(drawingData);
    const result = getDrawingTransformResult(currentDrawing, transform);
    if (result.status !== 'transformed') return {};

    return { drawing: result.drawing };
};

/**
 * Persist crop metadata without changing the canonical full-source variants.
 */
export const buildBackgroundRemovalCropChanges = ({
    element,
    imageOriginal,
    imageEditorData,
    clearDrawing,
    preserveBackgroundRemoval,
    drawingSessionTransform,
}: BuildBackgroundRemovalCropChangesOptions) => {
    const backgroundRemoval = getBackgroundRemoval(element);

    // Net flip applied this session: the scale being saved relative to the scale already baked into the
    // stored image. Used to transform the drawing so it stays aligned with the flipped image.
    const flipScaleX = getImageEditorScale(imageEditorData.scaleX) * getImageEditorDataScaleXOrDefault(element);
    const flipScaleY = getImageEditorScale(imageEditorData.scaleY) * getImageEditorDataScaleYOrDefault(element);

    return {
        imageOriginal,
        imageEditorData,
        backgroundRemoval: preserveBackgroundRemoval
            ? {
                  active: getActiveBackgroundRemovalVariant(element),
                  sources: getBackgroundRemovalSources(backgroundRemoval),
                  rendered: backgroundRemoval.rendered,
              }
            : null,
        file: null,
        media: null,
        // Persist the variant currently shown in the editor so the displayed image stays on that variant
        // through the save. Without this the top-level `image` keeps pointing at the previously-active
        // (e.g. with-background) render until the freshly cropped image finishes uploading, briefly flashing
        // the wrong variant on the board.
        ...(preserveBackgroundRemoval ? { image: getCurrentImage(element) } : {}),
        ...getDrawingSaveChanges(
            element,
            clearDrawing,
            drawingSessionTransform || { rotation: 0, scaleX: flipScaleX, scaleY: flipScaleY },
        ),
    };
};

type BuildBackgroundRemovalRevertChangesOptions = {
    element: ImMNElement;
    imageOriginal: MNImageContent;
    clearDrawing: boolean;
    preserveBackgroundRemoval: boolean;
};

/**
 * Revert to the full-source variant and clear cached cropped outputs.
 */
export const buildBackgroundRemovalRevertChanges = ({
    element,
    imageOriginal,
    clearDrawing,
    preserveBackgroundRemoval,
}: BuildBackgroundRemovalRevertChangesOptions) => {
    const backgroundRemoval = getBackgroundRemoval(element);

    // Reverting restores the unflipped original (scale 1), so the net flip is whatever scale was baked into
    // the stored image — applying it flips the drawing back into the original orientation.
    const flipScaleX = getImageEditorDataScaleXOrDefault(element);
    const flipScaleY = getImageEditorDataScaleYOrDefault(element);

    return {
        image: imageOriginal,
        imageOriginal: null,
        imageEditorData: null,
        backgroundRemoval: preserveBackgroundRemoval
            ? {
                  active: getActiveBackgroundRemovalVariant(element),
                  sources: getBackgroundRemovalSources(backgroundRemoval),
                  rendered: null,
              }
            : null,
        file: null,
        ...getDrawingSaveChanges(element, clearDrawing, { rotation: 0, scaleX: flipScaleX, scaleY: flipScaleY }),
    };
};

type BuildBackgroundRemovalRenderedChangesOptions = {
    element: ImMNElement;
    uploadedImage: MNImageContent;
    variant: BackgroundRemovalVariantOptions;
    additionalRenderedImage?: MNImageContent;
    additionalRenderedVariant?: BackgroundRemovalVariantOptions;
};

/**
 * Store a cropped rendered variant without overwriting the full-source variants.
 */
export const buildBackgroundRemovalRenderedChanges = ({
    element,
    uploadedImage,
    variant,
    additionalRenderedImage,
    additionalRenderedVariant,
}: BuildBackgroundRemovalRenderedChangesOptions): MNImageBackgroundRemovalContent => {
    const backgroundRemoval = getBackgroundRemoval(element);
    const rendered = {
        ...(backgroundRemoval.rendered || {}),
        [variant]: uploadedImage,
        ...(additionalRenderedImage && additionalRenderedVariant
            ? { [additionalRenderedVariant]: additionalRenderedImage }
            : {}),
    };

    return {
        active: getActiveBackgroundRemovalVariant(element),
        sources: getBackgroundRemovalSources(backgroundRemoval),
        rendered,
    };
};

/**
 * Build the background-removal source cache after the provider returns the transparent source.
 *
 * The processed image is persisted immediately so the provider is never called twice, but the original
 * variant stays active — applying the removed-background variant is deferred until the user saves.
 */
export const buildBackgroundRemovalCacheChanges = ({
    withBackgroundSource,
    withoutBackgroundSource,
}: {
    withBackgroundSource: MNImageContent;
    withoutBackgroundSource: MNImageContent;
}): { backgroundRemoval: MNImageBackgroundRemovalContent } => ({
    backgroundRemoval: {
        active: BackgroundRemovalVariantOptions.withBackground,
        sources: {
            withBackground: withBackgroundSource,
            withoutBackground: withoutBackgroundSource,
        },
    },
});

/**
 * Persist which background-removal variant is active without changing the currently displayed image.
 */
export const buildBackgroundRemovalActiveVariantChanges = ({
    element,
    active,
}: {
    element: ImMNElement;
    active: boolean;
}): {
    background?: null;
    showBackground?: false;
    backgroundRemoval: MNImageBackgroundRemovalContent;
} => {
    const variant = active
        ? BackgroundRemovalVariantOptions.withoutBackground
        : BackgroundRemovalVariantOptions.withBackground;

    return {
        ...(active ? { background: null, showBackground: false } : {}),
        backgroundRemoval: {
            ...getBackgroundRemoval(element),
            active: variant,
        },
    };
};

/**
 * Build top-level element changes when the user toggles between background variants.
 */
export const buildBackgroundRemovalToggleChanges = ({
    element,
    active,
}: {
    element: ImMNElement;
    active: boolean;
}): {
    image: MNImageContent;
    imageOriginal: MNImageContent | null;
    background?: null;
    showBackground?: false;
    backgroundRemoval: MNImageBackgroundRemovalContent;
} | null => {
    const variant = active
        ? BackgroundRemovalVariantOptions.withoutBackground
        : BackgroundRemovalVariantOptions.withBackground;
    const image = getBackgroundRemovalDisplayImage(element, variant);
    const imageOriginal = getBackgroundRemovalSource(element, variant);

    if (!image || !imageOriginal) return null;

    return {
        image,
        imageOriginal:
            getOriginalImageOrFallback(image) === getOriginalImageOrFallback(imageOriginal) ? null : imageOriginal,
        ...buildBackgroundRemovalActiveVariantChanges({ element, active }),
    };
};

/**
 * Resolve the changes that persist the staged background-removal variant after a crop/rotate save has run.
 *
 * Picks the right builder based on whether the just-saved element already sits on the staged variant:
 * - already on it (e.g. a crop moved the element onto that variant): persist only the variant/background
 *   flags via {@link buildBackgroundRemovalActiveVariantChanges}, leaving the displayed image untouched.
 * - not yet on it: do a full {@link buildBackgroundRemovalToggleChanges}, which also swaps the displayed
 *   image to the variant.
 *
 * Returns `null` when there is nothing to persist — the original (background-shown) variant is already
 * active — or when the toggle has no image to switch to.
 */
export const buildStagedBackgroundRemovalChanges = ({ element, active }: { element: ImMNElement; active: boolean }) => {
    // Nothing to apply when the original (background-shown) variant is already active.
    if (!active && !isBackgroundRemovalActive(element)) return null;

    const activeVariantAlreadyMatches = isBackgroundRemovalActive(element) === active;

    return activeVariantAlreadyMatches
        ? buildBackgroundRemovalActiveVariantChanges({ element, active })
        : buildBackgroundRemovalToggleChanges({ element, active });
};

/**
 * Apply the staged background-removal variant to an element in-memory so the editor can preview it without
 * persisting anything. Mirrors the reducer's shallow content merge, so the resulting element behaves exactly
 * as if the toggle had been dispatched — but the real (stored) element is left untouched until the user saves.
 */
export const applyStagedBackgroundRemoval = (element: ImMNElement, stagedActive: boolean): ImMNElement => {
    if (isBackgroundRemovalActive(element) === stagedActive) return element;

    const changes = buildBackgroundRemovalToggleChanges({ element, active: stagedActive });
    if (!changes) return element;

    const elementMap = element as unknown as ImmutableMap;

    return elementMap.update('content', (content: ImmutableMap) =>
        content.merge(Immutable.fromJS(changes)),
    ) as unknown as ImMNElement;
};

/**
 * Get the full-source image to use when resetting crop/rotation.
 */
export const getRevertedImage = (element: ImMNElement): MNImageContent | null =>
    getBackgroundRemovalSource(element, getActiveBackgroundRemovalVariant(element)) || null;
